import React, { useState, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import AppLayout from '../Layout/AppLayout';
import PartnerCard from './PartnerCard';

const PARTNER_TYPES = ['All', 'CBV', 'Broker', 'Lawyer', 'Accountant', 'Advisor'];

const TYPE_TO_CATEGORY = {
  CBV: 'cbv',
  Broker: 'business_broker',
  Lawyer: 'lawyer',
  Accountant: 'accountant',
  Advisor: 'cepa_coach',
};

// Reverse lookup: snake_case role → tab label. Used to seed the
// activeType filter from the controller-passed initialRoleFilter so
// "Find one →" deep-links select the right tab on mount.
const CATEGORY_TO_TYPE = Object.fromEntries(
  Object.entries(TYPE_TO_CATEGORY).map(([t, c]) => [c, t])
);

export default function PartnerDirectoryPage({
  partners = [],
  loading = false,
  persona = 'SELLER',
  userName = '',
  userInitials = '',
  planProgress = { completed: 0, total: 5, percentage: 0 },
  actionPlanBadge = 0,
  initialRoleFilter = null,
  country = 'CA',
}) {
  // A CBV is a Canadian credential; US owners see the same tab as "Valuator".
  const typeLabel = (type) => (type === 'CBV' && country === 'US' ? 'Valuator' : type);
  // Map controller-passed role to a known tab label, fall back to 'All'.
  // initialRoleFilter is already validated against PARTNER_CATEGORIES
  // server-side, but defensive lookup keeps a malformed prop safe.
  const initialTab = (initialRoleFilter && CATEGORY_TO_TYPE[initialRoleFilter]) || 'All';
  const [activeType, setActiveType] = useState(initialTab);
  const [regionFilter, setRegionFilter] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');

  // Derive unique regions and industries from data
  const regions = useMemo(() => {
    const set = new Set();
    partners.forEach((p) => (p.geography_regions || []).forEach((r) => set.add(r)));
    return Array.from(set).sort();
  }, [partners]);

  const industries = useMemo(() => {
    const set = new Set();
    partners.forEach((p) => (p.industry_tags || []).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [partners]);

  // Filter partners
  const filtered = useMemo(() => {
    let result = partners;

    if (activeType !== 'All') {
      const cat = TYPE_TO_CATEGORY[activeType];
      if (cat) result = result.filter((p) => p.partner_category === cat);
    }

    if (regionFilter) {
      result = result.filter((p) => (p.geography_regions || []).includes(regionFilter));
    }

    if (industryFilter) {
      result = result.filter((p) => (p.industry_tags || []).includes(industryFilter));
    }

    return result;
  }, [partners, activeType, regionFilter, industryFilter]);

  return (
    <AppLayout
      breadcrumb="PARTNER DIRECTORY"
      persona={persona}
      userName={userName}
      userInitials={userInitials}
      planProgress={planProgress}
      actionPlanBadge={actionPlanBadge}
    >
      {/* Sticky filter bar */}
      <div className="sticky top-14 z-10 bg-card/80 border-b border-border backdrop-blur-[20px] py-4 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Partner type pills */}
            <div className="flex items-center gap-2">
              {PARTNER_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setActiveType(type)}
                  className={`rounded-full px-4 py-2 text-[13px] font-medium transition-colors ${
                    activeType === type
                      ? 'bg-foreground text-background'
                      : 'border border-border text-muted-foreground hover:bg-accent'
                  }`}
                >
                  {typeLabel(type)}
                </button>
              ))}
            </div>

            {/* Region dropdown */}
            <div className="relative">
              <select
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
                className="appearance-none bg-transparent border border-border rounded-[8px] text-muted-foreground px-4 py-2 pr-10 text-[13px] cursor-pointer hover:bg-accent transition-colors"
              >
                <option value="">All Regions</option>
                {regions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            </div>

            {/* Industry dropdown */}
            <div className="relative">
              <select
                value={industryFilter}
                onChange={(e) => setIndustryFilter(e.target.value)}
                className="appearance-none bg-transparent border border-border rounded-[8px] text-muted-foreground px-4 py-2 pr-10 text-[13px] cursor-pointer hover:bg-accent transition-colors"
              >
                <option value="">All Industries</option>
                {industries.map((i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-8 py-12">
        {/* Page label */}
        <div className="flex items-center gap-2 mb-6">
          <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))] shadow-[0_0_8px_hsl(var(--succeedo-orange))]" />
          <span className="font-mono text-[11px] text-muted-foreground uppercase tracking-wide">
            PARTNER DIRECTORY
          </span>
        </div>

        {/* Headline */}
        <h1 className="font-serif font-light text-[42px] text-foreground tracking-[-0.02em] leading-tight mb-3">
          Find Your Advisory Team
        </h1>
        <p className="text-[17px] text-muted-foreground mb-12 max-w-3xl">
          Connect with vetted professionals who specialize in business succession. From valuation experts to M&amp;A lawyers, build your deal team with confidence.
        </p>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12 text-muted-foreground">Loading partners...</div>
        )}

        {/* Empty state. The directory only lists partners who serve the
            owner's country (PartnerMatchService.serving), so a US owner can
            have none yet: say so rather than blame the filters. */}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-12 text-muted-foreground" data-testid="partners-empty">
            {partners.length === 0 && country === 'US'
              ? "We don't have partners serving the US yet. George can still help you work out who you need."
              : 'No partners match your filters.'}
          </div>
        )}

        {/* Partner cards grid */}
        {!loading && filtered.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((partner) => (
              <PartnerCard
                key={partner.id}
                partner={partner}
                onClick={() => { window.location.href = `/succeedo/partners/${partner.id}`; }}
              />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
